import { Combobox } from '../components/Combobox'
import { Flag } from './Flag'
import { useLocale, useT } from './LocaleProvider'
import { LOCALES, LOCALE_INFO, isLocale } from './types'

export interface LanguageSelectorProps {
  className?: string
  /** The accessible name. Given in English AND in Mongolian by default — see below. */
  label?: string
}

/**
 * The language chooser, for every app's header.
 *
 * It is a `Combobox` and not a control of its own: two options today, and the day a third
 * language arrives it already has a filter field, keyboard handling and a listbox that a
 * screen reader understands. A bespoke dropdown for two options is a bespoke dropdown to
 * maintain.
 *
 * **The label is bilingual by default** — "Language · Хэл". A chooser labelled only in the
 * language you cannot currently read is a chooser you cannot find, which is the one control
 * on the page where that matters most.
 *
 * Each option shows the flag, the language in its own script, and — where they differ — the
 * English name after it. A Mongolian reader looks for "Монгол"; somebody who has landed in
 * the wrong language by accident looks for "English".
 */
export function LanguageSelector({ className, label }: LanguageSelectorProps) {
  const { locale, setLocale } = useLocale()
  const t = useT()

  return (
    <Combobox
      label={label ?? t('ul.language.label')}
      // Two options, so these are never shown — but they are required, and a component
      // that quietly supplies its own English is the thing that rule exists to stop.
      emptyLabel={t('ul.combobox.any')}
      searchPlaceholder={t('ul.combobox.filter')}
      noMatchLabel={t('ul.combobox.noMatch')}
      hideLabel
      className={className ? `ul-language ${className}` : 'ul-language'}
      icon={<Flag locale={locale} />}
      searchable={false}
      /*
       * Right-anchored. The chooser is a narrow control near the right edge of every
       * header, and the panel has to be wider than it — "Монгол · Mongolian" is three times
       * the trigger's width. Hung from the left it laid that surplus across whatever sat
       * beside it, which on Partners is Saved, Sign in and the category strip below.
       */
      align="end"
      options={LOCALES.map((code) => ({
        value: code,
        // The flag on every option, not only on the trigger. Without it the thing you are
        // choosing from does not look like the thing you have chosen, and a flag is what
        // identifies a language at a glance to somebody who cannot read either row.
        icon: <Flag locale={code} />,
        label:
          LOCALE_INFO[code].nativeName === LOCALE_INFO[code].englishName
            ? LOCALE_INFO[code].nativeName
            : `${LOCALE_INFO[code].nativeName} · ${LOCALE_INFO[code].englishName}`,
      }))}
      value={[locale]}
      onChange={(values) => {
        const next = values[0]
        // Choosing the language you are already in must not clear it — `Combobox` toggles
        // on single-select, so an empty array here means "the same one again".
        if (isLocale(next)) setLocale(next)
      }}
    />
  )
}

/**
 * The sentence a reader sees when they have asked for a language the app does not have yet.
 *
 * Mongolian is a real option in the selector and its catalogue is empty, so choosing it
 * shows English. **Saying nothing would be the lie** — a reader would assume the page simply
 * has little on it, or that the site is broken. This says which language they are looking at
 * and that the translation is coming.
 *
 * It renders nothing at all once the catalogue is complete, so it disappears on the day the
 * translation lands rather than needing to be found and removed.
 *
 * ## `{language}` is interpolated with the ENGLISH name, and only the English string uses it
 *
 * The English sentence names the language in English — "the Mongolian translation" — which
 * is what an English reader who has just chosen Монгол in the selector needs to see.
 *
 * A translated copy of this sentence names its own language in its own words instead, and
 * needs no placeholder at all: `mn.json`'s copy is reachable only by a reader who chose
 * Mongolian, so `{language}` there could only ever mean Mongolian. `replace` on a string
 * without the placeholder is a no-op, so nothing here has to know which case it is in. A
 * third language would be the same again: its file is read only by its own readers.
 */
export function LocaleNotice() {
  const { locale, translated, coverage } = useLocale()
  const t = useT()
  if (translated) return null

  const info = LOCALE_INFO[locale]
  const percent = Math.round(coverage * 100)
  // Two sentences rather than one with a conditional clause in it: "0% done" is a phrasing
  // no translator would choose, and a language with nothing in it deserves its own words.
  const key = percent > 0 ? 'ul.locale.notice.partial' : 'ul.locale.notice.notReady'
  const message = t(key)
    .replace('{language}', info.englishName)
    .replace('{percent}', String(percent))

  return (
    <div className="ul-locale-notice" role="status">
      <Flag locale={locale} />
      <p>{message}</p>
    </div>
  )
}
